import { zodResolver } from "@hookform/resolvers/zod";
import {
	getPartQueryOptions,
	listStockMovementsQueryOptions,
} from "@packages/api-client/hooks/equipmentController";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ArrowDown, ArrowUp, Edit, Package } from "lucide-react";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAdjustPartStock } from "@/api/equipment";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { NumberField } from "@/components/ui/number-field";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import { SubmitButton } from "@/components/ui/submit-button";
import { Textarea } from "@/components/ui/textarea";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { parseDecimalInput } from "@/utils/decimal";
import {
	type StockAdjustmentFormData,
	stockAdjustmentSchema,
} from "../-components/partFormSchema";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/parts/$partId/",
)({
	// Parts reference offline is roadmap Tier 2 (step 7); stock adjustments are
	// conflict-prone (rule 2) — online-only until that slice.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	loader: async ({ context, params }) => {
		await context.queryClient.ensureQueryData(
			getPartQueryOptions(params.orgId, params.partId),
		);
	},
	component: PartDetailPage,
});

function PartDetailPage() {
	const { orgId, partId } = Route.useParams();
	const { t } = useTranslation();
	const { formatDateOnly } = useDateFormatter();
	const navigate = Route.useNavigate();
	const [showAdjustDialog, setShowAdjustDialog] = useState(false);

	const { data: part } = useSuspenseQuery(getPartQueryOptions(orgId, partId));

	const { data: movementsData } = useSuspenseQuery(
		listStockMovementsQueryOptions(orgId, partId),
	);

	const adjustMutation = useAdjustPartStock();

	if (!part) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">{t("equipment.parts.notFound")}</p>
			</div>
		);
	}

	const isLowStock =
		part.minimumStock != null && part.currentQuantity <= part.minimumStock;

	return (
		<div className="flex flex-col gap-4 h-full">
			<PageHeader
				title={part.name}
				showBackButton
				actions={
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setShowAdjustDialog(true)}
						>
							<Package className="h-4 w-4 mr-1" />
							{t("equipment.parts.adjustStock")}
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() =>
								navigate({
									to: "/app/org/$orgId/equipment/parts/$partId/edit",
									params: { orgId, partId },
								})
							}
						>
							<Edit className="h-4 w-4 mr-1" />
							{t("common.edit")}
						</Button>
					</div>
				}
			/>

			{/* Low Stock Alert */}
			{isLowStock && (
				<div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">
					<AlertTriangle className="h-4 w-4 shrink-0" />
					{t("equipment.parts.lowStockAlert", {
						current: part.currentQuantity,
						minimum: part.minimumStock,
					})}
				</div>
			)}

			{/* Profile Card */}
			<Card>
				<CardContent className="pt-5 pb-4 space-y-4">
					<div className="flex flex-wrap items-center gap-x-5 gap-y-2">
						<Badge variant="secondary">{part.partNumber}</Badge>
						{part.category && <Badge variant="outline">{part.category}</Badge>}
						<span className="h-4 w-px bg-border" />
						<div className="flex items-center gap-1.5 text-sm">
							<Package className="h-3.5 w-3.5 text-muted-foreground" />
							<span className="text-muted-foreground">
								{t("equipment.parts.quantity")}:
							</span>
							<span className={`font-medium ${isLowStock ? "text-error" : ""}`}>
								{part.currentQuantity}
								{part.unitOfMeasure ? ` ${part.unitOfMeasure}` : ""}
							</span>
						</div>
					</div>

					<dl className="grid gap-x-6 gap-y-2 grid-cols-2 sm:grid-cols-3 text-sm">
						{part.minimumStock != null && (
							<div>
								<dt className="text-muted-foreground">
									{t("equipment.parts.minimumStock")}
								</dt>
								<dd className="font-medium">{part.minimumStock}</dd>
							</div>
						)}
						{part.unitCost != null && (
							<div>
								<dt className="text-muted-foreground">
									{t("equipment.parts.unitCost")}
								</dt>
								<dd className="font-medium">${part.unitCost.toFixed(2)}</dd>
							</div>
						)}
						{part.location && (
							<div>
								<dt className="text-muted-foreground">
									{t("equipment.parts.location")}
								</dt>
								<dd className="font-medium">{part.location}</dd>
							</div>
						)}
						{part.oemPartNumbers && part.oemPartNumbers.length > 0 && (
							<div className="col-span-2 sm:col-span-3">
								<dt className="text-muted-foreground">
									{t("equipment.parts.oemPartNumbers")}
								</dt>
								<dd className="font-medium">
									{part.oemPartNumbers.join(", ")}
								</dd>
							</div>
						)}
					</dl>
				</CardContent>
			</Card>

			{/* Stock Movements */}
			<Card>
				<CardContent className="pt-5">
					<h3 className="text-sm font-semibold mb-3">
						{t("equipment.parts.stockMovements")}
					</h3>
					{(movementsData?.data ?? []).length === 0 ? (
						<p className="text-sm text-base-content/50 py-4 text-center">
							{t("equipment.parts.noMovements")}
						</p>
					) : (
						<div className="space-y-2">
							{(movementsData?.data ?? []).map((movement) => (
								<div
									key={movement.id}
									className="flex items-center justify-between p-2 rounded-lg border border-base-300"
								>
									<div className="flex items-center gap-2">
										{movement.quantity > 0 ? (
											<ArrowUp className="h-4 w-4 text-success" />
										) : (
											<ArrowDown className="h-4 w-4 text-error" />
										)}
										<div>
											<p className="text-sm font-medium">
												{movement.quantity > 0 ? "+" : ""}
												{movement.quantity}{" "}
												<span className="text-base-content/60">
													({movement.previousQuantity} &rarr;{" "}
													{movement.newQuantity})
												</span>
											</p>
											{movement.reason && (
												<p className="text-xs text-base-content/60">
													{movement.reason}
												</p>
											)}
										</div>
									</div>
									<div className="text-right text-xs text-base-content/60">
										<p>
											{[
												movement.performedBy?.firstName,
												movement.performedBy?.lastName,
											]
												.filter(Boolean)
												.join(" ")}
										</p>
										<p>
											{formatDateOnly(new Date(movement.createdAt).getTime())}
										</p>
									</div>
								</div>
							))}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Stock Adjustment Dialog */}
			<StockAdjustmentDialog
				open={showAdjustDialog}
				onOpenChange={setShowAdjustDialog}
				onSubmit={(quantity, reason) =>
					adjustMutation
						.mutateAsync({
							organizationId: orgId,
							partId,
							data: { quantity, reason },
						})
						.then(() => setShowAdjustDialog(false))
				}
				isSubmitting={adjustMutation.isPending}
			/>
		</div>
	);
}

function StockAdjustmentDialog({
	open,
	onOpenChange,
	onSubmit,
	isSubmitting,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (quantity: number, reason: string) => Promise<unknown> | undefined;
	isSubmitting: boolean;
}) {
	const { t } = useTranslation();
	const formId = useId();
	const form = useForm<StockAdjustmentFormData>({
		resolver: zodResolver(stockAdjustmentSchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: { quantity: "", reason: "" },
	});

	const onValid = async (data: StockAdjustmentFormData) => {
		// Safe non-null: the schema guarantees the quantity parses.
		await onSubmit(parseDecimalInput(data.quantity) as number, data.reason);
		form.reset();
	};

	const guardedSubmit = useGuardedSubmit(form, onValid);

	return (
		<ResponsiveFormDialog
			open={open}
			onOpenChange={onOpenChange}
			title={t("equipment.parts.adjustStock")}
			pending={isSubmitting}
			footer={
				<>
					<Button
						type="button"
						variant="outline"
						onClick={() => onOpenChange(false)}
					>
						{t("common.cancel")}
					</Button>
					<SubmitButton form={formId} pending={isSubmitting}>
						{t("equipment.parts.adjustStock")}
					</SubmitButton>
				</>
			}
		>
			<Form {...form}>
				<form id={formId} onSubmit={guardedSubmit} className="space-y-4">
					<FormField
						control={form.control}
						name="quantity"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("equipment.parts.adjustQuantity")}
								</FormLabel>
								<FormControl>
									<NumberField
										placeholder={t("equipment.parts.adjustQuantityPlaceholder")}
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										name={field.name}
										ref={field.ref}
									/>
								</FormControl>
								<FormDescription>
									{t("equipment.parts.adjustQuantityHelp")}
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="reason"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("equipment.parts.adjustReason")}
								</FormLabel>
								<FormControl>
									<Textarea
										placeholder={t("equipment.parts.adjustReasonPlaceholder")}
										rows={2}
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormRootMessage />
				</form>
			</Form>
		</ResponsiveFormDialog>
	);
}
